import { Reducer, useCallback, useEffect, useReducer, useRef } from "react";

export type Middleware<S, A> = (state: S, action?: A) => void;

/**
 * A custom version of the React `useReducer` hook that allows you to add middleware functions that
 * run after the reducer. These middleware functions are called with the previous state, the next
 * state, and the action that triggered the state change.
 */
export function useReducerWithMiddleware<S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
  middleware: Middleware<S, A>[],
): [S, (action: A) => void] {
  const [state, dispatch] = useReducer(reducer, initialState);
  const actionRef = useRef<A>();

  const dispatchWithMiddleware = useCallback((action: A) => {
    actionRef.current = action;
    dispatch(action);
  }, []);

  useEffect(() => {
    if (!actionRef.current) {
      return;
    }
    for (const middlewareFn of middleware) {
      middlewareFn(state, actionRef.current);
    }
    actionRef.current = undefined;
  }, [middleware, state]);

  return [state, dispatchWithMiddleware];
}
